import { useEffect, useState } from "react";
import { Flex, FlexProps, Progress, Text } from "@chakra-ui/react";

import { useQuizSnapshot } from "../internal/state";
import { getPosInBounds } from "../internal/utils";
import { ContainerPropsOverride } from "./types";

export function ProgressIndicator({
  showTitle = true,
  progressBarOverrides,
  ...rest
}: FlexProps & {
  showTitle?: boolean;
  progressBarOverrides?: ContainerPropsOverride["progressBar"];
}) {
  const snap = useQuizSnapshot();
  const [colorOverrides, setColorOverrides] = useState<
    ContainerPropsOverride["progressBar"] | undefined
  >(progressBarOverrides ?? {});

  // override colors when slide change
  useEffect(() => {
    if (!snap.currentSlide) {
      return;
    }

    if (!snap.currentSlide.quizContainerProps?.progressBar) {
      return;
    }

    setColorOverrides(
      snap.currentSlide.quizContainerProps?.progressBar as ContainerPropsOverride["progressBar"],
    );
  }, [snap.currentSlide]);

  const colorScheme = colorOverrides?.colorScheme ?? "white";
  const activeSegmentBg = colorOverrides?.activeSegmentBg ?? "whiteAlpha.400";
  const inactiveSegmentBg = colorOverrides?.inactiveSegmentBg ?? "whiteAlpha.400";
  const textColor = colorOverrides?.textColor ?? "text.main";

  return (
    <Flex px={4} py={0} direction={"column"} gap={1} alignItems={"center"} {...rest}>
      {showTitle && (
        <Flex width={"full"} fontSize={"sm"} justifyContent={"space-between"}>
          <Text color={textColor} fontWeight={"bold"}>
            {snap.currentSegment?.title}
          </Text>
        </Flex>
      )}

      {/* <Text ml={"auto"} fontWeight={"semibold"} fontSize={"sm"} color={"blackAlpha.600"}>
        {snap.currentIdx + 1}/{snap.slideCount}
      </Text> */}

      <Flex direction={"row"} gap={2} width={"100%"}>
        {snap.segmentsFull.map((s, idx) => {
          const posInBounds = getPosInBounds(snap.currentIdx, s.bounds);
          const segmentIsActive = posInBounds === "inside";
          const idxInBound = snap.currentIdx - s.bounds[0];
          const segmentProgress = ((idxInBound + 1) / s.slideCount) * 100;

          return (
            <Flex
              key={idx}
              direction={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              flex={s.slideCount / snap.slideCount}
            >
              <Progress
                borderRadius={"full"}
                width={"full"}
                colorScheme={colorScheme}
                background={segmentIsActive ? activeSegmentBg : inactiveSegmentBg}
                value={posInBounds === "left" ? 0 : posInBounds === "right" ? 100 : segmentProgress}
                height={"4px"}
              />
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}
